import { type ReactNode, type Ref, useMemo } from 'react'
import cx from 'classnames'
import { CustomSelect } from '@/components/inputs/CustomSelect'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import styles from './SingleItemSelector.module.css'

export interface SingleItemSelectorProps<T> {
  value: T | undefined
  itemOptions: T[]
  itemToString: (item: T) => string
  itemPrefix?: (item: T) => ReactNode
  onChange: (item: T | undefined) => void
  placeholder?: string
  isDisabled?: boolean
  testId?: string
}

/**
 * A searchable select for a single item.
 */
export function SingleItemSelector<T>({
  value,
  itemOptions,
  itemToString,
  itemPrefix,
  onChange,
  placeholder,
  isDisabled,
  testId,
}: SingleItemSelectorProps<T>) {
  // Keyed by the labels rather than `itemToString`, which callers usually
  // pass as an inline lambda; rebuilding the source restarts the search.
  const labelsKey = itemOptions
    .map((item) => itemToString(item).toLowerCase())
    .join('\u0000')
  const itemSource = useMemo(() => {
    const labels = labelsKey.split('\u0000')
    const labelByItem = new Map(itemOptions.map((item, i) => [item, labels[i]]))
    return ItemSource.static(itemOptions, (item, text) =>
      (labelByItem.get(item) ?? '').includes(text.toLowerCase()),
    )
  }, [itemOptions, labelsKey])
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource,
  })
  return (
    <CustomSelect
      {...selectProps}
      value={value}
      itemToString={itemToString}
      onChange={onChange}
      multi={false}
      placeholder={placeholder}
      isDisabled={isDisabled}
      testId={testId}
      renderItem={({
        item,
        toggleMenu: _toggleMenu,
        focusInputField: _focusInputField,
        removeItem: _removeItem,
        isDisabled: _isDisabled,
        ...itemProps
      }) => (
        <SelectItem prefix={itemPrefix?.(item)} {...itemProps}>
          {itemToString(item)}
        </SelectItem>
      )}
      renderSearchItem={({ item, ...itemProps }) => (
        <SelectItem prefix={itemPrefix?.(item)} {...itemProps}>
          {itemToString(item)}
        </SelectItem>
      )}
    />
  )
}

export interface SelectItemProps {
  children?: ReactNode
  highlighted?: boolean
  selected?: boolean
  prefix?: ReactNode
  itemToString: unknown
  ref?: Ref<HTMLDivElement>
  testId?: string
}

/**
 * An option in a single item selector.
 */
export function SelectItem({
  children,
  highlighted,
  selected,
  prefix,
  itemToString: _itemToString,
  ref,
  testId,
  ...rest
}: SelectItemProps) {
  return (
    <div
      data-test={testId}
      className={cx(
        styles.selectItem,
        highlighted && styles.highlighted,
        selected && styles.selected,
      )}
      ref={ref}
      {...rest}
    >
      {prefix && <div className={styles.prefix}>{prefix}</div>}
      <div className={styles.selectItemText}>{children}</div>
    </div>
  )
}
